// Duration parsing/formatting. Canonical unit is INTEGER MINUTES.
//
// Accepted input forms (per spec): 1.5, 1:30, 90m, 1h30 — plus the obvious
// neighbours (1h, 45m, 1h30m, .5, "1h 30m"). A BARE number is interpreted as
// DECIMAL HOURS (2 -> 120min, 0.25 -> 15min), consistent with the app's
// decimal-hours display unit. To enter raw minutes, suffix with `m` (90m).

export function parseDuration(input: string): number | null {
  const raw = input.trim().toLowerCase().replace(/\s+/g, '')
  if (raw === '') return 0 // empty clears the cell to zero

  // 1h30 / 1h30m / 1h
  let m = raw.match(/^(\d+)h(\d+)m?$/)
  if (m) return toInt(Number(m[1]) * 60 + Number(m[2]))

  m = raw.match(/^(\d+)h$/)
  if (m) return toInt(Number(m[1]) * 60)

  // 90m
  m = raw.match(/^(\d+)m$/)
  if (m) return toInt(Number(m[1]))

  // 1:30  (h:mm)
  m = raw.match(/^(\d+):([0-5]?\d)$/)
  if (m) return toInt(Number(m[1]) * 60 + Number(m[2]))

  // bare decimal hours: 1.5, 2, .75
  m = raw.match(/^(\d*\.?\d+)$/)
  if (m) return toInt(Number(m[1]) * 60)

  return null
}

function toInt(n: number): number {
  return Math.round(n)
}

// minutes -> decimal-hours string, trailing zeros trimmed (for editable cells).
export function formatHours(minutes: number): string {
  const h = minutes / 60
  return trimZeros(h.toFixed(2))
}

// minutes -> fixed 2-decimal hours (for columns that must align vertically).
export function formatHours2(minutes: number): string {
  return (minutes / 60).toFixed(2)
}

function trimZeros(s: string): string {
  return s.replace(/\.?0+$/, '')
}

// 'HH:MM' start/end -> minutes. Returns 0 if either is missing or end<=start.
export function minutesFromStartEnd(start: string | null, end: string | null): number {
  if (!start || !end) return 0
  const s = hhmmToMinutes(start)
  const e = hhmmToMinutes(end)
  if (s == null || e == null) return 0
  const diff = e - s
  return diff > 0 ? diff : 0
}

function hhmmToMinutes(v: string): number | null {
  const m = v.trim().match(/^(\d{1,2}):([0-5]\d)$/)
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (h > 23) return null
  return h * 60 + min
}
